import { useCallback, useEffect, useState } from 'react';
import {
  getPlanningClients,
  getPlanningDeliverables,
  getPlanningInterventions,
  getPlanningProjects,
  getPlanningTasks,
} from '../planningReferences.api';

const emptyReferences = { projects: [], interventions: [], realizations: [], tasks: [], clients: [], deliverables: [] };

export default function usePlanningReferences(projectId) {
  const [references, setReferences] = useState(emptyReferences);
  const [loading, setLoading] = useState(true);
  const [deliverablesLoading, setDeliverablesLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [projects, interventions, tasks, clients] = await Promise.all([
          getPlanningProjects({ signal: controller.signal }),
          getPlanningInterventions({ signal: controller.signal }),
          getPlanningTasks({ signal: controller.signal }),
          getPlanningClients({ signal: controller.signal }),
        ]);
        if (!controller.signal.aborted) {
          const realizations = [...projects, ...interventions].sort((a, b) => String(a.displayLabel || '').localeCompare(String(b.displayLabel || '')));
          setReferences((current) => ({ ...current, projects, interventions, realizations, tasks, clients }));
        }
      } catch (loadError) {
        if (loadError.name !== 'AbortError') setError(loadError.message || 'Impossible de charger les références métier.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, []);

  const loadDeliverables = useCallback(async (selectedProjectId, signal) => {
    if (!selectedProjectId) {
      setReferences((current) => ({ ...current, deliverables: [] }));
      return;
    }
    try {
      setDeliverablesLoading(true);
      const deliverables = await getPlanningDeliverables(selectedProjectId, { signal });
      if (!signal?.aborted) setReferences((current) => ({ ...current, deliverables }));
    } catch (loadError) {
      if (loadError.name !== 'AbortError') setError(loadError.message || 'Impossible de charger les livrables du projet.');
    } finally {
      if (!signal?.aborted) setDeliverablesLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadDeliverables(projectId, controller.signal);
    return () => controller.abort();
  }, [projectId, loadDeliverables]);

  return { ...references, loading, deliverablesLoading, error, loadDeliverables };
}
